import React from "react"
import { isMobileOnly } from "react-device-detect"
import clsx from "clsx"
import { useTranslation } from "react-i18next"

import FormElement from "components/Form/FormElement"
import { isVATNumberValid, isRnaValid, isSiretValid, isNotEmpty } from "utils/validation"
import { defaultEntity as defaultOperator, operators } from "utils/constants/operators"
import { useAuth } from "hooks/useAuth"
import {
	AM_GENDER,
	COLLECTIVITE_GENDER,
	HORS_AM_GENDER,
	VELOCISTE_GENDER,
	apeAssociationList,
	apeCompanyList,
	entityGenderList,
	genderStoreList,
	partnerChainList,
} from "utils/constants"
import { LocalEntity } from "models"
import { FormError } from "models/form"

import { CheckOutlineIcon } from "components/Elements/Icons/CheckOutline"

type StructureFormProps = {
	partner: LocalEntity
	setUpdatedPartner: React.Dispatch<React.SetStateAction<Partial<LocalEntity>>>
	updatedPartner: LocalEntity
	errors: Array<FormError>
	setErrors: React.Dispatch<React.SetStateAction<Array<FormError>>>
	isEdit: boolean
	editCallback: React.Dispatch<React.SetStateAction<Partial<LocalEntity>>>
	showLocalErrors: boolean
	setShowLocalErrors: React.Dispatch<React.SetStateAction<boolean>>
	isPartnerAlreadyRegistered: boolean
}

const FRENCH_CODE = ["FR"] // Quand MAJ de Salesforce => ["FR", "RE", "YT", "GP", "MQ", "YT"] // Inclunding 5 DOM : Guadeloupe, Martinique, Guyane, Réunion and Mayotte
const SIRET_SIZE = 14
export const StructureForm = ({
	partner,
	updatedPartner,
	setUpdatedPartner,
	setErrors,
	isEdit,
	showLocalErrors,
	isPartnerAlreadyRegistered,
}: StructureFormProps) => {
	const { t } = useTranslation()
	const { user, isAdminOperator } = useAuth()

	const defaultFormFields: Partial<LocalEntity> = React.useMemo(
		() => ({
			gender: 0,
			isFub: false,
			raison_sociale: "",
			code_ape: "",
			nom_commercial: "",
			isFranchise: false,
			structureChainName: "",
			isMaker: false,
			rna: "",
			registre_date: new Date(),
			registre_volume: "",
			registre_folio: "",
			tva_intra: "",
			siret: "",
		}),
		[]
	)

	const [formFields, setFormFields] = React.useState(defaultFormFields)

	const isFrenchCountrySelected = updatedPartner?.pays
		? FRENCH_CODE.includes(updatedPartner?.pays)
		: FRENCH_CODE.includes(partner?.pays)

	React.useEffect(
		() => {
			// When partner reloaded (after await fetchData()), set data in formFields
			if (partner || !isEdit) setFormFields(partner)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[partner, isEdit]
	)

	React.useEffect(
		() => {
			// When we create a new partner, we have to pass data to parent directly.
			// We don't have any submit button on this component in this case
			if (isEdit)
				setUpdatedPartner((prevFormFields) => ({
					...prevFormFields,
					gender: formFields.gender,
					isFub: formFields.isFub,
					raison_sociale: formFields.raison_sociale.trim(),
					code_ape: formFields.code_ape,
					nom_commercial: formFields.nom_commercial.trim(),
					isFranchise: formFields.isFranchise,
					structureChainName: formFields.structureChainName,
					isMaker: formFields.isMaker,
					rna: formFields.rna,
					registre_date: formFields.registre_date,
					registre_volume: formFields.registre_volume,
					registre_folio: formFields.registre_folio,
					tva_intra: formFields.tva_intra,
					siret: formFields.siret,
				}))
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isEdit, formFields]
	)

	const handleInputChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>, type: string, fieldName) => {
			const target = event.target
			let value: string | boolean | React.ChangeEvent<HTMLInputElement>
			let name: string

			if (!target && (type = "date")) {
				name = fieldName
				value = event
			} else {
				value = target.type === "checkbox" ? target.checked : target.value
				name = target.name
			}

			setFormFields((s) => ({
				...s,
				[name]: value,
			}))
		},
		[]
	)

	const handleChangeSiret = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value.replace(/\s+/g, "")
		const newValue = value.slice(0, SIRET_SIZE)

		setFormFields((prev) => ({
			...prev,
			siret: newValue,
		}))
	}, [])

	const handleValidation = React.useCallback((name: string, errs: FormError[]) => {
		setErrors((s) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleInputChange,
	}

	// En création tous les champs sont éditables
	// En édition, les admins opérateurs ne peuvent modifier que le siret et la raison sociale. Tous les autres champs sont en readonly
	// Les admins ocode peuvent modifier tous les champs
	const isFieldDisabledForUser = !isEdit || (isAdminOperator && isPartnerAlreadyRegistered)

	return (
		<section id="structure" className="py-16 px-4 scroll-mt-10">
			<h2 className="c-h2 font-semibold mb-7 uppercase">
				{t("store.structure", { ns: "partner" })}
			</h2>
			<div className="l-grid u-mg-negative-hz-m">
				<div className="lg:w-1/2 w-full p-5">
					{/* Type de structure */}

					{isPartnerAlreadyRegistered ? (
						<div className="c-form-group mb-5">
							<label className="c-label mb-5">
								{t("type", { ns: "partner" })} :{" "}
							</label>
							<div
								className={clsx("flex text-xs", {
									"flex-col items-start": isMobileOnly,
									"items-center": !isMobileOnly,
								})}
							>
								{t(
									`${
										genderStoreList.find((g) => g.value === formFields.gender)
											?.label
									}`
								)}
								{formFields.est_bicytrust && (
									<div className={clsx("flex", { "ml-4": !isMobileOnly })}>
										<CheckOutlineIcon
											size={20}
											color="#4caf50"
											className={""}
										/>
										BicyTrust
									</div>
								)}
							</div>
						</div>
					) : (
						<FormElement
							{...formElementCommonProps}
							value={formFields.gender}
							name="gender"
							type="radio"
							options={genderStoreList.filter((g) => {
								const userOperatorId = entityGenderList.find(
									(e) => e.value === user.gender
								)?.operator_id
								const correspondingOperator = [defaultOperator, ...operators].find(
									(o) => o.id === userOperatorId
								)
								return correspondingOperator?.allowed_gender.includes(g.value)
							})}
							classNameOptionsContainer="flex-col"
							classNameLabel="mb-5"
							classNameOption="flex-1 items-center"
							label={t("type", { ns: "partner" })}
							required={true}
						/>
					)}
				</div>
				<div className="lg:w-1/2 w-full p-5">
					{/* FUB member */}
					{(formFields.gender === HORS_AM_GENDER || formFields.gender === AM_GENDER) && ( // Blocks appear if association selected, gender 1 or 2
						<FormElement
							{...formElementCommonProps}
							value={formFields.isFub}
							name="isFub"
							type="radio"
							label={t("edit.afub-member", { ns: "partner" })}
							options={[
								{ id: "afub-true", value: "true", label: "global.yes" },
								{ id: "afub-false", value: "false", label: "global.no" },
							]}
							classNameOptionsContainer="flex-row w-full"
							classNameLabel="mb-5"
							classNameOption="flex-1 items-center"
							required={true}
							readonly={isFieldDisabledForUser}
						/>
					)}
				</div>
				<div className="lg:w-1/2 w-full p-5">
					{/* raison_sociale */}
					<FormElement
						{...formElementCommonProps}
						value={formFields.raison_sociale}
						name="raison_sociale"
						type="text"
						label={t("store.structure_name", { ns: "partner" })}
						className="mb-5"
						required={true}
						readonly={!isEdit}
					/>
				</div>
				<div className="lg:w-1/2 w-full p-5">
					{/* code_ape */}
					<FormElement
						{...formElementCommonProps}
						value={formFields.code_ape}
						defaultValue={{
							label:
								(formFields.gender === VELOCISTE_GENDER
									? apeCompanyList
									: apeAssociationList
								).find((b) => b.value === formFields.code_ape)?.label ||
								formFields.code_ape,
							value: formFields.code_ape,
						}}
						options={
							formFields.gender === VELOCISTE_GENDER
								? apeCompanyList
								: apeAssociationList
						}
						name="code_ape"
						id="code_ape"
						type="select"
						label={t("store.structure_code_ape", { ns: "partner" })}
						className="mb-5"
						required={true}
						readonly={isFieldDisabledForUser}
					/>
				</div>

				<div className="lg:w-1/2 w-full p-5">
					{/* nom_commercial */}
					<FormElement
						{...formElementCommonProps}
						value={formFields.nom_commercial}
						name="nom_commercial"
						type="text"
						label={t("store.structure_commercial_name", { ns: "partner" })}
						className="mb-5"
						required={true}
						readonly={isFieldDisabledForUser}
					/>
				</div>

				{(formFields.gender === VELOCISTE_GENDER ||
					formFields.gender === COLLECTIVITE_GENDER) && ( // Blocks appear if velociste or collectivite selected, gender 0 or 3
					<>
						<div className="lg:w-1/2 w-full p-5">
							{/* siret */}
							<FormElement
								{...formElementCommonProps}
								onChange={handleChangeSiret}
								value={formFields.siret}
								name="siret"
								type="text"
								label={t("store.structure_siret", { ns: "partner" })}
								className="mb-5"
								required={isFrenchCountrySelected}
								customRules={[
									{
										key: "siret-invalid",
										check: (value) =>
											isNotEmpty(value) ? value.length === SIRET_SIZE : true,
										message: t("store.siretNotValid", { ns: "partner" }),
									},
								]}
								readonly={!isEdit}
							/>
						</div>
						<div className="lg:w-1/2 w-full p-5">
							{/* VAT N° */}
							<FormElement
								{...formElementCommonProps}
								value={formFields.tva_intra}
								name="tva_intra"
								type="text"
								maxLength={14}
								label={t("store.vatNumber", { ns: "partner" })}
								className="mb-5"
								required={!isFrenchCountrySelected}
								customRules={[
									{
										key: "vat-invalid",
										check: (value) => {
											return (
												(!isFrenchCountrySelected &&
													isVATNumberValid(value)) ||
												true
											)
										},
										message: t("store.invalidVatNumber", { ns: "partner" }),
									},
								]}
								readonly={isFieldDisabledForUser}
							/>
						</div>
						<div className="lg:w-1/2 w-full"></div>
					</>
				)}

				{formFields.gender === VELOCISTE_GENDER && ( // Blocks appear if velociste selected, gender 0
					<>
						<div className="lg:w-1/2 w-full p-5">
							{/* Chaine / Franchise */}
							<FormElement
								{...formElementCommonProps}
								value={formFields.isFranchise}
								name="isFranchise"
								type="radio"
								label={t("isFranchise", { ns: "partner" })}
								options={[
									{
										id: "franchise-true",
										value: "true",
										label: "global.yes",
									},
									{
										id: "franchise-false",
										value: "false",
										label: "global.no",
									},
								]}
								className="mb-7"
								classNameOptionsContainer={clsx("flex-row w-full", {
									"flex-col": isMobileOnly,
								})}
								classNameLabel="mb-5"
								classNameOption="flex-1 items-center"
								readonly={isFieldDisabledForUser}
								disabled={false}
							/>
						</div>

						<div className="lg:w-1/2 w-full p-5">
							{/* structureChainName */}
							{formFields.isFranchise && (
								<FormElement
									{...formElementCommonProps}
									value={formFields.structureChainName}
									defaultValue={{
										label:
											partnerChainList.find(
												(chain) =>
													chain.value === formFields.structureChainName
											)?.label || "",
										value: formFields.structureChainName,
									}}
									options={partnerChainList}
									name="structureChainName"
									id="structureChainName"
									type="select"
									label={t("store.structure_chain_name", { ns: "partner" })}
									className="mb-5"
									required={false}
									readonly={isFieldDisabledForUser}
									disabled={false}
								/>
							)}
						</div>

						<div className="lg:w-1/2 w-full p-5">
							{/* Fabricant / Marque  */}
							<FormElement
								{...formElementCommonProps}
								value={formFields.isMaker}
								name="isMaker"
								type="radio"
								label={t("isMaker", { ns: "partner" })}
								options={[
									{ id: "maker-true", value: "true", label: "global.yes" },
									{ id: "maker-false", value: "false", label: "global.no" },
								]}
								className=""
								classNameOptionsContainer={clsx("flex-row w-full", {
									"flex-col": isMobileOnly,
								})}
								classNameLabel="mb-5"
								classNameOption="flex-1 items-center"
								readonly={isFieldDisabledForUser}
								disabled={false}
							/>
						</div>
					</>
				)}

				{/* Hors Alsace et Moselle  */}
				{formFields.gender === HORS_AM_GENDER && (
					<>
						<div className="lg:w-1/2 w-full p-5">
							{/* rna */}
							<FormElement
								{...formElementCommonProps}
								value={formFields.rna}
								name="rna"
								type="text"
								maxLength="9"
								label={t("edit.rna", { ns: "partner" })}
								className="mb-5"
								required={true}
								customRules={[
									{
										key: "rna-invalid",
										check: (value: string) => {
											return (
												(value && value.length > 1 && isRnaValid(value)) ||
												true
											)
										},
										message: "Le numéro RNA n'est pas valide.",
									},
								]}
								readonly={isFieldDisabledForUser}
							/>
						</div>
					</>
				)}

				{formFields.gender === AM_GENDER && ( // Gender 2
					<>
						<div className="w-full p-5">
							<h3>{t("edit.register_number", { ns: "partner" })}</h3>
						</div>

						<div className="lg:w-1/4 w-full p-5">
							{/* Date */}
							<FormElement
								{...formElementCommonProps}
								value={formFields.registre_date}
								name="registre_date"
								type="date"
								label={t("edit.date", { ns: "partner" })}
								className="mb-5"
								required={true}
								disabled={isFieldDisabledForUser}
							/>
						</div>

						<div className="lg:w-1/4 w-full p-5">
							{/* volume */}
							<FormElement
								{...formElementCommonProps}
								value={formFields.registre_volume}
								name="registre_volume"
								type="text"
								label={t("edit.volume", { ns: "partner" })}
								className="mb-5"
								required={true}
								maxLength="3"
								readonly={isFieldDisabledForUser}
							/>
						</div>

						<div className="lg:w-1/4 w-full p-5">
							{/* folio */}
							<FormElement
								{...formElementCommonProps}
								value={formFields.registre_folio}
								name="registre_folio"
								type="text"
								label={t("edit.folio", { ns: "partner" })}
								className="mb-5"
								required={true}
								maxLength="3"
								readonly={isFieldDisabledForUser}
							/>
						</div>
					</>
				)}
			</div>
		</section>
	)
}
